import React, { useState } from 'react';
import { X, Copy, Check, Download, Layers, Palette, Layout, Sparkles, Sun, Moon } from 'lucide-react';
import { Logo } from './Logo';

interface BrandGuidelinesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BrandGuidelinesModal: React.FC<BrandGuidelinesModalProps> = ({
  isOpen,
  onClose
}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>('light');

  if (!isOpen) return null;

  const colorPalette = [
    { name: 'Primary Navy', hex: '#001B48', rgb: 'rgb(0, 27, 72)', usage: 'Headings, "Uniform" wordmark, primary buttons, header banner' },
    { name: 'Royal Blue', hex: '#2563EB', rgb: 'rgb(37, 99, 235)', usage: '"Loop" wordmark, accents, interactive links, active states' },
    { name: 'Pure White', hex: '#FFFFFF', rgb: 'rgb(255, 255, 255)', usage: 'Backgrounds, shirt motif, high-contrast dark text' },
    { name: 'Light Grey', hex: '#F8FAFC', rgb: 'rgb(248, 250, 252)', usage: 'Card fills, subtle borders, background canvas neutral' },
    { name: 'Slate Subtext', hex: '#64748B', rgb: 'rgb(100, 116, 139)', usage: '"IMMINGHAM" subtext, secondary captions, metadata' }
  ];

  const variants = [
    {
      id: 'horizontal',
      name: '2. Website Header Logo (Primary Navigation)',
      description: 'Horizontal arrangement for website and app headers (~320×70px). Features the vector icon with vertically aligned clean wordmark and uppercase IMMINGHAM.',
      render: (theme: 'light' | 'dark') => <Logo variant="horizontal" size="md" theme={theme} />,
      spec: '320 × 70 px recommended | Transparent background | No tagline'
    },
    {
      id: 'full',
      name: '1. Primary Brand Logo (Full Stacked)',
      description: 'Full version for splash screens, posters, marketing, and formal document headers. Includes full tagline badge.',
      render: (theme: 'light' | 'dark') => <Logo variant="full" size="md" theme={theme} showTagline />,
      spec: 'Stacked layout | Full tagline badge | High impact'
    },
    {
      id: 'compact',
      name: '4. Compact Logo (Tablet & Mid-width)',
      description: 'A streamlined horizontal layout designed for tablets, sidebars, or tighter headers.',
      render: (theme: 'light' | 'dark') => <Logo variant="compact" size="md" theme={theme} />,
      spec: '[Icon] Uniform Loop | No location subtext'
    },
    {
      id: 'small',
      name: '5. Small Navigation Logo (Sticky Headers)',
      description: 'Ultra-compact version specifically designed for 40-50px tall sticky or scrolled navigation bars.',
      render: (theme: 'light' | 'dark') => <Logo variant="small" theme={theme} />,
      spec: '36px Icon height | Tight spacing | 40-50px bars'
    },
    {
      id: 'icon',
      name: '3. Icon Only (App Icon / Favicon)',
      description: 'Pure circular recycling arrows enclosing the blazer and shirt. Identifiable down to 32px.',
      render: (theme: 'light' | 'dark') => <Logo variant="icon" size="lg" theme={theme} />,
      spec: 'Favicon (32px), PWA (192px / 512px) | Transparent SVG'
    }
  ];

  const handleCopyCode = (index: number) => {
    const svgCode = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">...</svg>`;
    navigator.clipboard.writeText(svgCode);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md animate-fade-in">
      <div className="relative w-full max-w-4xl max-h-[92dvh] flex flex-col bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden">
        
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[#001B48] text-white border-b border-blue-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-500/20 border border-blue-400/30">
              <Sparkles className="w-5 h-5 text-blue-300" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-black tracking-tight text-white">
                Uniform Loop Brand Guidelines System
              </h2>
              <p className="text-xs text-blue-200">
                Responsive Brand Assets, Typography & Color Specifications
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Theme Toggle for Preview */}
            <button
              onClick={() => setPreviewTheme(previewTheme === 'light' ? 'dark' : 'light')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white/10 hover:bg-white/20 text-white transition-colors border border-white/20"
            >
              {previewTheme === 'light' ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
              <span>{previewTheme === 'light' ? 'Preview Dark' : 'Preview Light'}</span>
            </button>

            <button
              onClick={onClose}
              className="p-2 rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50/50">
          
          {/* Section 1: Responsive Logo Variants */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Layout className="w-5 h-5 text-blue-600" />
                <h3 className="text-base font-heading font-black text-slate-900">
                  1. The 5 Responsive Logo Variants
                </h3>
              </div>
              <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-3 py-1 rounded-full border border-slate-200">
                Pure SVG Vector
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {variants.map((v, idx) => (
                <div
                  key={v.id}
                  className={`p-5 rounded-2xl border transition-all ${
                    previewTheme === 'dark'
                      ? 'bg-slate-900 border-slate-800 text-white'
                      : 'bg-white border-slate-200 shadow-sm text-slate-900'
                  }`}
                >
                  <div className="text-xs font-bold uppercase tracking-wider text-blue-500 mb-1">
                    Variant {idx + 1}
                  </div>
                  <h4 className="text-sm font-heading font-bold mb-1">
                    {v.name}
                  </h4>
                  <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                    {v.description}
                  </p>

                  {/* Live Render Canvas */}
                  <div className={`p-6 rounded-xl flex items-center justify-center border ${
                    previewTheme === 'dark' ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-100'
                  }`}>
                    {v.render(previewTheme)}
                  </div>

                  {/* Spec Footer */}
                  <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[11px] text-slate-400">
                    <span>{v.spec}</span>
                    <a
                      href="/logo.png"
                      download={`${v.id}-logo.png`}
                      className="inline-flex items-center gap-1 font-bold text-blue-600 hover:text-blue-700"
                    >
                      <Download className="w-3.5 h-3.5" /> Image
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2: Color Palette */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-5 h-5 text-blue-600" />
              <h3 className="text-base font-heading font-black text-slate-900">
                2. Brand Color Palette Specs
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {colorPalette.map((c) => (
                <div key={c.name} className="p-3 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
                  <div>
                    <div
                      className="h-14 rounded-xl shadow-inner mb-2.5 border border-slate-200/50"
                      style={{ backgroundColor: c.hex }}
                    />
                    <div className="font-heading font-bold text-xs text-slate-900">{c.name}</div>
                    <div className="text-[11px] font-mono text-slate-600 font-semibold">{c.hex}</div>
                    <div className="text-[10px] text-slate-400">{c.rgb}</div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-500 leading-tight">
                    {c.usage}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: Typography & Usage Rules */}
          <div className="p-5 bg-blue-50/60 rounded-2xl border border-blue-100">
            <div className="flex items-center gap-2 mb-2 text-blue-950 font-heading font-black text-sm">
              <Layers className="w-4 h-4 text-blue-600" />
              <span>Typography & Clear Space Guidelines</span>
            </div>
            <ul className="text-xs text-slate-600 space-y-1.5 list-disc pl-5 leading-relaxed">
              <li><strong>Clean Wordmark:</strong> "Uniform" is rendered in solid Primary Navy (`#001B48`), and "Loop" in solid Royal Blue (`#2563EB`). No heart shape substitutions in the OO wordmark to preserve modern SaaS readability.</li>
              <li><strong>Location Subtext:</strong> "IMMINGHAM" is formatted in uppercase Slate Grey (`#64748B`) with `tracking-[0.2em]`.</li>
              <li><strong>No Text Gradients:</strong> Gradients appear strictly inside the vector blazer icon. All wording remains clean, flat, and legible.</li>
              <li><strong>Clear Space:</strong> Maintain a minimum clear space equal to half the icon height around all sides of the header logo.</li>
            </ul>
          </div>

        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-white border-t border-slate-200 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            Uniform Loop Immingham • Master Vector Brand System v2.0
          </span>
          <button
            onClick={onClose}
            className="px-5 py-2 text-xs font-bold text-white bg-[#001B48] hover:bg-blue-900 rounded-full transition-colors shadow-sm"
          >
            Close Guidelines
          </button>
        </div>

      </div>
    </div>
  );
};
